/**
 * Klaviyo Form block
 *
 * Loading: header.php lazy loads klaviyo.js on first interaction (or after 4s).
 * A page built around a form can't wait that long, so klaviyo.js is loaded as
 * soon as a block's form is near the viewport.
 *
 * Fallback: when the script is blocked (Brave Shields, ad blockers) or the form
 * never renders, the block's fallback message is shown in its place.
 *
 * "Use site layout" override:
 * Klaviyo renders every field as a [data-testid="form-row"] inside a single
 * flex column. When the block's site layout is on, this tags that column with
 * .kf-grid and gives each row a data-kf-span (12-column grid, see
 * klaviyo_form.scss) so short fields pair up on desktop. Mobile stays stacked.
 *
 * Klaviyo renders late (lazy loaded) and re-renders on validation and on
 * multi-step forms, so rows are re-tagged whenever the form's DOM changes.
 */

(function () {
  'use strict';

  const LONG_LABEL = 40; // Characters; longer questions get the full width.
  const SHORT_LABEL = 12; // Short dropdowns like "Title" sit beside two fields.
  const FALLBACK_DELAY = 8000; // ms after loading starts before the form counts as missing.

  function isVisible(el) {
    return el.getClientRects().length > 0;
  }

  function rowSize(row) {
    if (row.querySelectorAll('[data-testid="form-component"]').length > 1) {
      return 'full'; // Already laid out side by side in Klaviyo.
    }

    const controls = row.querySelectorAll('input:not([type="hidden"]), textarea, select');
    if (controls.length === 0) {
      return 'full'; // Text, images, submit button.
    }
    if (row.querySelector('input[type="checkbox"], input[type="radio"], textarea')) {
      return 'full';
    }

    // Label or placeholder only: an open dropdown renders its options inside the
    // row, so the row's full text would change the layout mid-selection.
    const labelText = Array.from(row.querySelectorAll('label'), (el) => el.textContent.trim()).join(' ');
    const label = Math.max(labelText.length, (controls[0].placeholder || '').length);
    if (label > LONG_LABEL) {
      return 'full';
    }

    const isDropdown = row.querySelector('[aria-haspopup="listbox"]');
    return isDropdown && label <= SHORT_LABEL ? 'small' : 'half';
  }

  function spansFor(sizes) {
    const spans = [];
    const pairable = (size) => size === 'half' || size === 'small';

    for (let i = 0; i < sizes.length; ) {
      if (sizes[i] === 'small' && sizes[i + 1] === 'half' && sizes[i + 2] === 'half') {
        spans.push(2, 5, 5);
        i += 3;
      } else if (pairable(sizes[i]) && pairable(sizes[i + 1])) {
        spans.push(6, 6);
        i += 2;
      } else {
        spans.push(12); // Full rows, and a half row left without a partner.
        i += 1;
      }
    }
    return spans;
  }

  function layout(wrapper) {
    const rows = Array.from(wrapper.querySelectorAll('[data-testid="form-row"]'));
    if (rows.length === 0) {
      return;
    }

    const grid = rows[0].parentElement;
    if (!grid.classList.contains('kf-grid')) {
      grid.classList.add('kf-grid');
    }

    const visible = rows.filter((row) => row.parentElement === grid && isVisible(row));
    const spans = spansFor(visible.map(rowSize));

    visible.forEach((row, i) => {
      const span = String(spans[i]);
      if (row.getAttribute('data-kf-span') !== span) {
        row.setAttribute('data-kf-span', span);
      }
    });
  }

  function setFallback(wrapper, show) {
    const fallback = wrapper.querySelector('.klaviyo-form-block__fallback');
    if (!fallback) {
      return;
    }
    fallback.hidden = !show;
    wrapper.classList.toggle('is-fallback', show);
  }

  // Only reveal the fallback while Klaviyo still hasn't rendered the form.
  function showFallbackIfEmpty(wrapper) {
    if (!wrapper.querySelector('form')) {
      setFallback(wrapper, true);
    }
  }

  function update(wrapper) {
    if (wrapper.querySelector('form')) {
      setFallback(wrapper, false); // Klaviyo got there after all.
    }
    if (wrapper.getAttribute('data-kf-layout') === 'site') {
      layout(wrapper);
    }
  }

  function watch(wrapper) {
    let queued = false;
    const schedule = () => {
      if (queued) {
        return;
      }
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        update(wrapper);
      });
    };

    new MutationObserver(schedule).observe(wrapper, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'], // Conditional fields shown or hidden by Klaviyo.
    });
    schedule();
  }

  function whenNear(wrappers, callback) {
    if (!('IntersectionObserver' in window)) {
      callback();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          callback();
        }
      },
      { rootMargin: '600px 0px' }
    );
    wrappers.forEach((wrapper) => observer.observe(wrapper));
  }

  function init() {
    const wrappers = document.querySelectorAll('.klaviyo-form-block__form');
    if (wrappers.length === 0) {
      return;
    }

    wrappers.forEach(watch);

    const showFallbacks = () => wrappers.forEach(showFallbackIfEmpty);

    // Script blocked outright (header.php's onerror): show the fallback straight away.
    if (window.klaviyoBlocked) {
      showFallbacks();
    }
    window.addEventListener('klaviyo:blocked', showFallbacks);

    whenNear(wrappers, () => {
      if (typeof window.loadKlaviyo === 'function') {
        window.loadKlaviyo();
      }
      // Loaded but never rendered (form removed in Klaviyo, script stripped, etc.).
      setTimeout(showFallbacks, FALLBACK_DELAY);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
